import { memo, type ReactNode } from 'react';
import type {
  ProjectPermissions,
  Resource,
  SharePermissions,
  WorkspacePermissions,
} from '@frameio/graphql-client/generated';
import {
  type AccountPermissionsCheck,
  useAccountPermissions,
  useProjectDerivedSharePermissions,
  useProjectPermissions,
  useWorkspacePermissions,
} from '../../hooks';

type ProjectPermissionsCheck = keyof Omit<ProjectPermissions, '__typename'>;
type WorkspacePermissionsCheck = keyof Omit<WorkspacePermissions, '__typename'>;
type ProjectDerivedSharePermissionsCheck = keyof Omit<
  SharePermissions,
  '__typename'
>;

export type PermissionsGateProps = {
  /** The children to conditionally render based on your permissions check */
  children: ReactNode;
  /** Optionally provide a component to render in the event a permissions check fails */
  fallback?: ReactNode;
  /** The id of the Resource from which you want to read permissions */
  id: Resource['id'];
  /** Whether to render the children initially prior to permissions being available */
  isInitiallyVisible?: boolean;
} & (
  | {
      /** The permission field you want to check */
      check: AccountPermissionsCheck;
      /** The Entity type from which you want to read a permission */
      checkOn: 'Account';
    }
  | {
      /** The permission field you want to check */
      check: ProjectPermissionsCheck;
      /** The Entity type from which you want to read a permission */
      checkOn: 'Project';
    }
  | {
      /** The permission field you want to check */
      check: WorkspacePermissionsCheck;
      /** The Entity type from which you want to read a permission */
      checkOn: 'Workspace';
    }
  | {
      /** The permission field you want to check */
      check: ProjectDerivedSharePermissionsCheck;
      /** The Entity type from which you want to read a permission */
      checkOn: 'Share';
    }
);

const AccountPermissionsGate = ({
  check,
  children,
  fallback,
  id,
  isInitiallyVisible,
}: Omit<PermissionsGateProps, 'check' | 'checkOn'> & {
  check: AccountPermissionsCheck;
}) => {
  const permissionCheck = useAccountPermissions({
    accountId: id,
    check,
    isInitiallyPermitted: isInitiallyVisible,
  });

  return permissionCheck ? children : <>{fallback}</>;
};

const ProjectPermissionsGate = memo(
  ({
    check,
    children,
    fallback,
    id,
    isInitiallyVisible,
  }: Omit<PermissionsGateProps, 'check' | 'checkOn'> & {
    check: ProjectPermissionsCheck;
  }) => {
    const permissionCheck = useProjectPermissions({
      check,
      isInitiallyPermitted: isInitiallyVisible,
      projectId: id,
    });

    return permissionCheck ? children : <>{fallback}</>;
  }
);

const WorkspacePermissionsGate = ({
  check,
  children,
  fallback,
  id,
  isInitiallyVisible,
}: Omit<PermissionsGateProps, 'check' | 'checkOn'> & {
  check: WorkspacePermissionsCheck;
}) => {
  const permissionCheck = useWorkspacePermissions({
    check,
    isInitiallyPermitted: isInitiallyVisible,
    workspaceId: id,
  });

  return permissionCheck ? children : <>{fallback}</>;
};

const ProjectDerivedSharePermissionsGate = ({
  check,
  children,
  fallback,
  id,
  isInitiallyVisible,
}: Omit<PermissionsGateProps, 'check' | 'checkOn'> & {
  check: ProjectDerivedSharePermissionsCheck;
}) => {
  const permissionCheck = useProjectDerivedSharePermissions({
    check,
    isInitiallyPermitted: isInitiallyVisible,
    shareId: id,
  });
  return permissionCheck ? children : <>{fallback}</>;
};

/**
 * Wraps a component to conditionally render based on whether it fails or succeeds a permissions check.
 * Provide the Entity type and its id and the permission will be read off the cache.
 * This is intended for use within nested components to avoid prop drilling.
 * You **must** query the permissions you want ahead of time.
 * It is your responsibility to query the specific permission fields you need.
 */
export const PermissionsGate = memo(
  ({ check, children, checkOn, ...rest }: PermissionsGateProps) => {
    switch (checkOn) {
      case 'Account':
        return (
          <AccountPermissionsGate
            check={check as AccountPermissionsCheck}
            {...rest}
          >
            {children}
          </AccountPermissionsGate>
        );
      case 'Project':
        return (
          <ProjectPermissionsGate
            check={check as ProjectPermissionsCheck}
            {...rest}
          >
            {children}
          </ProjectPermissionsGate>
        );
      case 'Workspace':
        return (
          <WorkspacePermissionsGate
            check={check as WorkspacePermissionsCheck}
            {...rest}
          >
            {children}
          </WorkspacePermissionsGate>
        );

      case 'Share':
        return (
          <ProjectDerivedSharePermissionsGate
            check={check as ProjectDerivedSharePermissionsCheck}
            {...rest}
          >
            {children}
          </ProjectDerivedSharePermissionsGate>
        );
      default:
        return null;
    }
  }
);
